import { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { PageHeader, Card, CardHeader, AtomBadge } from "../components/primitives";
import { Mail, ShieldCheck, CheckCircle2, AlertTriangle, Copy, RefreshCw, Sliders } from "lucide-react";

const TABS = ["Profile", "Thresholds", "Email & domains", "Localisation"];

const RULES = [
  { code: "RULE-STR-001", name: "Structuring (just below threshold)", current: 5_000_000, amltrix: "ML.T1003" },
  { code: "RULE-VEL-012", name: "Velocity anomaly", current: 10_000_000, amltrix: "ML.T0506" },
  { code: "RULE-SMR-007", name: "Smurfing — mule consolidation", current: 8_000_000, amltrix: "ML.T1007" },
  { code: "RULE-NCS-031", name: "New customer high-value inflow", current: 15_000_000, amltrix: "ML.T0102" },
];

function ThresholdSimulator() {
  const [rule, setRule] = useState(RULES[0]);
  const [newTh, setNewTh] = useState(rule.current);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { setNewTh(rule.current); setResult(null); }, [rule]);

  const simulate = async (th) => {
    setLoading(true);
    try {
      const r = await api.post("/rules/simulate", { rule: rule.code, current_threshold: rule.current, new_threshold: th });
      setResult(r.data);
    } finally { setLoading(false); }
  };

  const fmt = (n) => "₦" + (n / 1_000_000).toFixed(1) + "M";

  return (
    <Card>
      <CardHeader
        title="Threshold simulator"
        subtitle="Drag the slider to project how a threshold change would impact alert volume"
        right={<AtomBadge model="Atom Proton" version="v2.4" />}
      />
      <div className="p-4 space-y-4">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-ink-subtle mb-2">Rule</div>
          <div className="grid grid-cols-2 gap-2">
            {RULES.map(r => (
              <button
                key={r.code}
                data-testid={`sim-rule-${r.code}`}
                onClick={() => setRule(r)}
                className={`text-left px-3 py-2 rounded-lg border ${rule.code === r.code ? "border-amber/60 bg-amber/8" : "border-line hover:bg-bg-hover"}`}
              >
                <div className="text-[12px] font-medium">{r.code}</div>
                <div className="text-[10px] text-ink-subtle">{r.name} · {r.amltrix}</div>
                <div className="text-[10px] text-ink-muted mt-0.5">Current: <span className="tabular font-medium">{fmt(r.current)}</span></div>
              </button>
            ))}
          </div>
        </div>

        <div className="border-t border-line pt-4">
          <div className="flex items-center justify-between text-[11px] mb-2">
            <span className="text-ink-subtle">New threshold</span>
            <div className="flex items-center gap-3">
              <span className="text-[10px] text-ink-subtle">Current: <span className="tabular font-medium text-ink">{fmt(rule.current)}</span></span>
              <span className="text-[14px] font-semibold tabular">{fmt(newTh)}</span>
            </div>
          </div>
          <input
            type="range"
            min={1_000_000}
            max={100_000_000}
            step={500_000}
            value={newTh}
            onChange={(e) => setNewTh(Number(e.target.value))}
            onMouseUp={() => simulate(newTh)}
            onTouchEnd={() => simulate(newTh)}
            className="w-full accent-amber"
            data-testid="sim-slider"
          />
          <div className="flex justify-between text-[10px] text-ink-subtle mt-1">
            <span>₦1M</span><span>₦25M</span><span>₦50M</span><span>₦100M</span>
          </div>
        </div>

        {(result || loading) && (
          <div className="border-t border-line pt-4 space-y-3">
            {loading ? (
              <div className="text-[12px] text-ink-muted">Simulating against 14,000 seeded transactions…</div>
            ) : (
              <>
                <div className="grid grid-cols-4 gap-2">
                  <div className="p-3 bg-bg-warm rounded-lg">
                    <div className="text-[10px] uppercase tracking-wider text-ink-subtle">At current</div>
                    <div className="display text-[20px] font-semibold tabular">{result.current_alerts}</div>
                    <div className="text-[10px] text-ink-subtle">alerts/90d</div>
                  </div>
                  <div className="p-3 bg-amber/10 rounded-lg border border-amber/30">
                    <div className="text-[10px] uppercase tracking-wider text-ink-subtle">At new</div>
                    <div className="display text-[20px] font-semibold tabular text-amber-700">{result.projected_alerts}</div>
                    <div className="text-[10px] text-ink-subtle">alerts/90d</div>
                  </div>
                  <div className="p-3 bg-bg-warm rounded-lg">
                    <div className="text-[10px] uppercase tracking-wider text-ink-subtle">Change</div>
                    <div className={`display text-[20px] font-semibold tabular ${result.delta > 0 ? "text-risk-high" : result.delta < 0 ? "text-risk-low" : ""}`}>{result.delta > 0 ? "+" : ""}{result.delta}</div>
                    <div className="text-[10px] text-ink-subtle">{result.pct_change > 0 ? "+" : ""}{result.pct_change}%</div>
                  </div>
                  <div className="p-3 bg-bg-warm rounded-lg">
                    <div className="text-[10px] uppercase tracking-wider text-ink-subtle">Analyst load</div>
                    <div className="display text-[20px] font-semibold tabular">+{result.estimated_analyst_hours_added}</div>
                    <div className="text-[10px] text-ink-subtle">hours / period</div>
                  </div>
                </div>

                {/* Threshold curve */}
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-ink-subtle mb-2">Alert volume vs threshold curve</div>
                  <div className="grid grid-cols-7 gap-1">
                    {result.bins.map(b => {
                      const max = Math.max(...result.bins.map(x => x.alerts), 1);
                      const h = (b.alerts / max) * 70 + 10;
                      const isCur = b.threshold === rule.current;
                      const isNew = b.threshold === newTh;
                      return (
                        <div key={b.threshold} className="flex flex-col items-center gap-1">
                          <div className="text-[10px] tabular font-medium">{b.alerts}</div>
                          <div className={`w-full rounded-t ${isNew ? "bg-amber" : isCur ? "bg-navy" : "bg-bg-subtle"}`} style={{ height: `${h}px` }} />
                          <div className="text-[9px] text-ink-subtle tabular">{fmt(b.threshold)}</div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex items-center gap-3 text-[10px] text-ink-subtle mt-2">
                    <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-navy" /> Current threshold</span>
                    <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber" /> Simulated threshold</span>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="border-t border-line pt-3 flex items-center justify-between">
                  <div className="text-[11px] text-ink-muted">{result.amltrix} · {result.model}</div>
                  <div className="flex gap-2">
                    <button className="px-3 py-1.5 text-[11px] border border-line rounded-full hover:bg-bg-hover">Discard</button>
                    <button className="px-3 py-1.5 text-[11px] bg-ink text-white rounded-full hover:bg-navy">Propose to maker-checker</button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

function EmailDomains() {
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newDomain, setNewDomain] = useState("");
  const [selected, setSelected] = useState(null);
  const [adding, setAdding] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/email/domains");
      const list = r.data?.domains?.data || [];
      setDomains(list);
      if (!selected && list.length > 0) setSelected(list[0]);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const addDomain = async () => {
    if (!newDomain.match(/^[a-z0-9.-]+\.[a-z]{2,}$/i)) { alert("Enter a valid domain (e.g. mail.atom-aml.io)"); return; }
    setAdding(true);
    try {
      const r = await api.post("/email/domains", { name: newDomain.toLowerCase() });
      if (r.data.ok) {
        setNewDomain("");
        await load();
        setSelected(r.data.domain);
      } else {
        alert(r.data.error || "Could not add domain");
      }
    } finally { setAdding(false); }
  };

  const verify = async (id) => {
    try {
      await api.post(`/email/domains/${id}/verify`);
      await load();
    } catch (e) { alert("Verify failed: " + e.message); }
  };

  const copy = (text) => { navigator.clipboard.writeText(text); };

  const statusPill = (s) => {
    const map = { verified: "bg-risk-low/15 text-risk-low", pending: "bg-risk-medium/15 text-risk-medium", "not_started": "bg-bg-warm text-ink-muted", failed: "bg-risk-veryhigh/15 text-risk-veryhigh" };
    return <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${map[s] || "bg-bg-warm text-ink-muted"}`}>{s || "—"}</span>;
  };

  return (
    <Card>
      <CardHeader
        title="Email sender domains"
        subtitle="Verify a domain so PINs and notifications deliver from your own brand"
        right={<button onClick={load} className="text-[11px] text-amber-600 hover:underline inline-flex items-center gap-1"><RefreshCw size={10} strokeWidth={1.5} /> Reload</button>}
      />
      <div className="p-4 grid grid-cols-[300px_1fr] gap-4">
        {/* Domain list */}
        <div className="space-y-2">
          <div className="space-y-1.5 max-h-[360px] overflow-y-auto pr-1">
            {loading ? <div className="text-[12px] text-ink-muted p-2">Loading…</div> :
              domains.length === 0 ? <div className="text-[11px] text-ink-subtle p-2">No domains yet. Add one below.</div> :
              domains.map(d => (
                <button
                  key={d.id}
                  data-testid={`domain-${d.name}`}
                  onClick={() => setSelected(d)}
                  className={`w-full text-left p-2 rounded-lg border ${selected?.id === d.id ? "border-amber/60 bg-amber/8" : "border-line hover:bg-bg-hover"}`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[12px] font-medium truncate">{d.name}</span>
                    {statusPill(d.status)}
                  </div>
                  <div className="text-[10px] text-ink-subtle">Region: {d.region} · Created {(d.created_at || "").slice(0,10)}</div>
                </button>
              ))
            }
          </div>
          <div className="pt-2 border-t border-line space-y-1.5">
            <input
              data-testid="domain-input"
              type="text"
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              placeholder="mail.your-bank.com"
              className="w-full px-3 py-2 text-[12px] bg-bg-warm border border-line rounded-md focus:outline-none focus:ring-2 focus:ring-amber/30 focus:border-amber"
            />
            <button
              data-testid="domain-add"
              onClick={addDomain}
              disabled={adding}
              className="w-full px-3 py-2 text-[12px] bg-ink text-white rounded-md hover:bg-navy disabled:opacity-60"
            >
              {adding ? "Adding…" : "+ Add domain"}
            </button>
          </div>
        </div>

        {/* DNS records panel */}
        <div className="bg-bg-warm rounded-xl border border-line p-4 min-w-0">
          {!selected ? (
            <div className="text-[12px] text-ink-muted">Select a domain to see DNS records.</div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="display text-[15px] font-semibold tracking-tight">{selected.name}</div>
                  <div className="text-[11px] text-ink-subtle">Add the records below at your DNS provider. Verification typically takes 5–30 minutes.</div>
                </div>
                <div className="flex items-center gap-2">
                  {statusPill(selected.status)}
                  <button data-testid="domain-verify" onClick={() => verify(selected.id)} className="px-3 py-1 text-[11px] bg-ink text-white rounded-full hover:bg-navy">Verify</button>
                </div>
              </div>

              {selected.status === "verified" ? (
                <div className="p-4 rounded-lg border border-risk-low/40 bg-risk-low/5 flex items-start gap-3">
                  <CheckCircle2 size={18} className="text-risk-low shrink-0" strokeWidth={1.5} />
                  <div>
                    <div className="text-[13px] font-medium text-risk-low">Domain verified</div>
                    <div className="text-[11px] text-ink-muted">PINs, notifications, and STR receipts will now be sent from <span className="font-medium">no-reply@{selected.name}</span>.</div>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  {(selected.records || [
                    { record: "SPF", name: selected.name, type: "TXT", value: "v=spf1 include:amazonses.com ~all", ttl: "Auto", status: "pending" },
                    { record: "DKIM", name: `resend._domainkey.${selected.name}`, type: "TXT", value: "p=MIGfMA0GCSqGSIb3DQEBAQUAA4GNADCBiQKBgQD…(public key)…", ttl: "Auto", status: "pending" },
                    { record: "DMARC", name: `_dmarc.${selected.name}`, type: "TXT", value: "v=DMARC1; p=quarantine; rua=mailto:dmarc@" + selected.name, ttl: "Auto", status: "pending" },
                  ]).map((r, i) => (
                    <div key={i} className="p-3 bg-white rounded-lg border border-line">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-bg-warm font-medium tracking-wide">{r.type}</span>
                          <span className="text-[12px] font-medium">{r.record}</span>
                        </div>
                        {statusPill(r.status)}
                      </div>
                      <div className="text-[11px] grid grid-cols-[60px_1fr_auto] gap-x-2 gap-y-1">
                        <span className="text-ink-subtle">Name</span>
                        <span className="hash-mono break-all">{r.name}</span>
                        <button onClick={() => copy(r.name)} className="p-0.5 text-ink-subtle hover:text-ink" title="Copy"><Copy size={11} /></button>
                        <span className="text-ink-subtle">Value</span>
                        <span className="hash-mono break-all">{r.value}</span>
                        <button onClick={() => copy(r.value)} className="p-0.5 text-ink-subtle hover:text-ink" title="Copy"><Copy size={11} /></button>
                        <span className="text-ink-subtle">TTL</span>
                        <span className="tabular">{r.ttl || "Auto"}</span>
                        <span />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-4 pt-3 border-t border-line text-[10px] text-ink-subtle">
                <ShieldCheck size={10} strokeWidth={1.5} className="inline mr-1" />
                Records signed and stored by Resend. The Atom AML platform never sees the DKIM private key.
              </div>
            </>
          )}
        </div>
      </div>
    </Card>
  );
}

export default function Settings() {
  const { institution, switchInstitution } = useAuth();
  const [insts, setInsts] = useState([]);
  const [tab, setTab] = useState("Profile");
  useEffect(() => { api.get("/institutions").then(r => setInsts(r.data.institutions || [])); }, []);
  const inst = institution || insts[0];

  return (
    <div>
      <PageHeader title="Settings" subtitle="Configuration · maker-checker enforced" sections={["5.12"]} />
      <div className="px-6 pt-3 flex gap-1 border-b border-line">
        {TABS.map(t => (
          <button key={t} data-testid={`settings-tab-${t}`} onClick={() => setTab(t)} className={`px-3 py-2 text-[12px] border-b-2 ${tab === t ? "border-amber text-ink font-medium" : "border-transparent text-ink-subtle hover:text-ink"}`}>{t}</button>
        ))}
      </div>

      <div className="p-6 space-y-3">
        {tab === "Profile" && (
          <div className="grid grid-cols-2 gap-3">
            <Card>
              <CardHeader title="Institution profile" subtitle="View-as enables session-level reskinning" />
              <div className="p-4 space-y-2 text-[12px]">
                {inst && [
                  ["Name", inst.name],["RC", inst.rc],["FRC", inst.frc],["CBN licence", inst.cbn_licence],
                  ["MLRO", inst.mlro],["CEO", inst.ceo],["CCO", inst.cco],["Branches", inst.branches],
                  ["Staff", inst.staff?.toLocaleString()],["Customers", inst.customers?.toLocaleString()],
                  ["AUM", inst.aum],["Address", inst.address]
                ].map(([k,v]) => <div key={k} className="flex justify-between border-b border-line/50 py-1"><span className="text-ink-subtle">{k}</span><span className="text-right">{v}</span></div>)}
                <select value={inst?.id || ""} onChange={(e) => switchInstitution(insts.find(i => i.id === e.target.value))} className="w-full px-2 py-1 border border-line rounded-md text-[12px] mt-2">
                  {insts.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
                </select>
              </div>
            </Card>
            <Card>
              <CardHeader title="Working hours and holiday calendar" />
              <div className="p-3 text-[12px]">
                <div className="mb-2">Banking hours: 08:00 — 16:00 WAT (Mon–Fri)</div>
                <div className="text-[11px] text-ink-muted">Nigerian public holidays 2026 pre-loaded: New Year, Easter, Workers Day, Eid al-Fitr, Democracy Day, Eid al-Adha, Independence Day, Christmas, Boxing Day.</div>
              </div>
            </Card>
          </div>
        )}

        {tab === "Thresholds" && (
          <div className="grid grid-cols-[1fr_360px] gap-3">
            <ThresholdSimulator />
            <Card>
              <CardHeader title="Current thresholds" subtitle="Maker-checker required for changes" right={<Sliders size={12} className="text-ink-subtle" strokeWidth={1.5} />} />
              <div className="p-3 text-[11.5px] space-y-1">
                {[
                  ["CTR threshold", "₦5,000,000"],
                  ["Large transaction alert", "₦10,000,000"],
                  ["Tier 1 single transaction", "₦50,000"],
                  ["Tier 1 cumulative daily", "₦300,000"],
                  ["Tier 2 single transaction", "₦200,000"],
                  ["Tier 2 cumulative daily", "₦500,000"],
                  ["Sanctions auto-block match", "≥ 90%"],
                  ["Dormant account threshold", "180 days"],
                  ["FX outbound trigger", "USD 10,000"],
                  ["PEP enhanced screening", "Quarterly"],
                  ["Session auto-lock (idle)", "15 minutes"],
                  ["Audit log retention", "7 years"],
                ].map(([k,v]) => <div key={k} className="flex justify-between border-b border-line/50 py-1"><span className="text-ink-subtle">{k}</span><span className="font-medium tabular">{v}</span></div>)}
              </div>
            </Card>
          </div>
        )}

        {tab === "Email & domains" && <EmailDomains />}

        {tab === "Localisation" && (
          <div className="grid grid-cols-2 gap-3">
            <Card>
              <CardHeader title="Localisation" />
              <div className="p-3 text-[12px] space-y-2">
                <div>Language: <select className="border border-line rounded-md px-2 py-0.5"><option>English (Nigerian)</option><option disabled>Yoruba (planned)</option><option disabled>Igbo (planned)</option><option disabled>Hausa (planned)</option><option disabled>French (planned)</option></select></div>
                <div>Date format: <span className="font-medium">DD MMM YYYY</span></div>
                <div>Time format: <span className="font-medium">24-hour WAT</span></div>
                <div>Currency display: <span className="font-medium">NGN with ₦ symbol</span></div>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
